import { useState, useCallback, useMemo } from 'react';
import { Plus, X } from 'lucide-react';

type FilterOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'starts_with'
  | 'ends_with'
  | 'greater_than'
  | 'less_than'
  | 'between'
  | 'is_null'
  | 'is_not_null'
  | 'in';

type FilterCombiner = 'AND' | 'OR';

interface FilterCondition {
  id: string;
  column: string;
  operator: FilterOperator;
  value: string;
  value2?: string;
  combiner: FilterCombiner;
}

const OPERATOR_LABELS: Record<FilterOperator, string> = {
  equals: '=',
  not_equals: '\u2260',
  contains: 'contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  greater_than: '>',
  less_than: '<',
  between: 'between',
  is_null: 'is null',
  is_not_null: 'is not null',
  in: 'in',
};

const OPERATORS: FilterOperator[] = [
  'equals',
  'not_equals',
  'contains',
  'starts_with',
  'ends_with',
  'greater_than',
  'less_than',
  'between',
  'is_null',
  'is_not_null',
  'in',
];

/** Whether the operator requires no value input */
function isNullaryOperator(op: FilterOperator): boolean {
  return op === 'is_null' || op === 'is_not_null';
}

/** Whether the operator needs a second value */
function isBinaryOperator(op: FilterOperator): boolean {
  return op === 'between';
}

function filtersToWhere(filters: FilterCondition[]): string {
  const validFilters = filters.filter((f) => {
    if (!f.column) return false;
    if (isNullaryOperator(f.operator)) return true;
    if (!f.value.trim()) return false;
    if (isBinaryOperator(f.operator) && !f.value2?.trim()) return false;
    return true;
  });

  if (validFilters.length === 0) return '';

  return validFilters
    .map((f, i) => {
      const prefix = i > 0 ? ` ${f.combiner} ` : '';
      const col = `"${f.column.replace(/"/g, '""')}"`;
      const val = f.value.replace(/'/g, "''");

      switch (f.operator) {
        case 'equals':
          return `${prefix}${col} = '${val}'`;
        case 'not_equals':
          return `${prefix}${col} != '${val}'`;
        case 'contains':
          return `${prefix}${col} LIKE '%${val}%'`;
        case 'starts_with':
          return `${prefix}${col} LIKE '${val}%'`;
        case 'ends_with':
          return `${prefix}${col} LIKE '%${val}'`;
        case 'greater_than':
          return `${prefix}${col} > '${val}'`;
        case 'less_than':
          return `${prefix}${col} < '${val}'`;
        case 'between': {
          const val2 = (f.value2 ?? '').replace(/'/g, "''");
          return `${prefix}${col} BETWEEN '${val}' AND '${val2}'`;
        }
        case 'is_null':
          return `${prefix}${col} IS NULL`;
        case 'is_not_null':
          return `${prefix}${col} IS NOT NULL`;
        case 'in':
          return `${prefix}${col} IN (${f.value
            .split(',')
            .map((v) => `'${v.trim().replace(/'/g, "''")}'`)
            .join(', ')})`;
        default:
          return '';
      }
    })
    .join('');
}

let nextFilterId = 0;
function makeFilterId(): string {
  return `filter_${++nextFilterId}`;
}

interface FilterBuilderProps {
  columns: string[];
  onApply: (where: string) => void;
  initialWhere?: string;
}

export function FilterBuilder({ columns, onApply, initialWhere: _initialWhere }: FilterBuilderProps) {
  const [filters, setFilters] = useState<FilterCondition[]>([]);

  const handleAddFilter = useCallback(() => {
    setFilters((prev) => [
      ...prev,
      {
        id: makeFilterId(),
        column: columns[0] ?? '',
        operator: 'equals',
        value: '',
        combiner: 'AND',
      },
    ]);
  }, [columns]);

  const handleRemoveFilter = useCallback((id: string) => {
    setFilters((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const handleUpdateFilter = useCallback(
    (id: string, updates: Partial<FilterCondition>) => {
      setFilters((prev) =>
        prev.map((f) => (f.id === id ? { ...f, ...updates } : f)),
      );
    },
    [],
  );

  const whereClause = useMemo(() => filtersToWhere(filters), [filters]);

  const handleApply = useCallback(() => {
    onApply(whereClause);
  }, [onApply, whereClause]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleApply();
      }
    },
    [handleApply],
  );

  return (
    <div className="px-3 py-2 bg-dbgo-panel border-b flex flex-col gap-1.5">
      {filters.length === 0 && (
        <div className="text-2xs text-text-muted py-0.5">
          No filters. Click "Add Filter" to build a WHERE clause visually.
        </div>
      )}

      {filters.map((filter, index) => (
        <div key={filter.id} className="flex items-center gap-1.5 flex-wrap">
          {/* Combiner (AND/OR) - shown for 2nd+ filter */}
          {index > 0 ? (
            <select
              value={filter.combiner}
              onChange={(e) =>
                handleUpdateFilter(filter.id, {
                  combiner: e.target.value as FilterCombiner,
                })
              }
              className="bg-dbgo-input border rounded-sm px-1.5 py-0.5 text-2xs text-text-primary outline-none w-[52px] focus:border-focus"
            >
              <option value="AND">AND</option>
              <option value="OR">OR</option>
            </select>
          ) : (
            <span className="text-2xs text-text-muted w-[52px] text-center">WHERE</span>
          )}

          {/* Column dropdown */}
          <select
            value={filter.column}
            onChange={(e) =>
              handleUpdateFilter(filter.id, { column: e.target.value })
            }
            className="bg-dbgo-input border rounded-sm px-1.5 py-0.5 text-xs text-text-primary outline-none min-w-[100px] max-w-[160px] focus:border-focus"
          >
            {columns.length === 0 && (
              <option value="">No columns</option>
            )}
            {columns.map((col) => (
              <option key={col} value={col}>
                {col}
              </option>
            ))}
          </select>

          {/* Operator dropdown */}
          <select
            value={filter.operator}
            onChange={(e) =>
              handleUpdateFilter(filter.id, {
                operator: e.target.value as FilterOperator,
              })
            }
            className="bg-dbgo-input border rounded-sm px-1.5 py-0.5 text-xs text-text-primary outline-none min-w-[90px] focus:border-focus"
          >
            {OPERATORS.map((op) => (
              <option key={op} value={op}>
                {OPERATOR_LABELS[op]}
              </option>
            ))}
          </select>

          {/* Value input(s) */}
          {!isNullaryOperator(filter.operator) && (
            <input
              type="text"
              value={filter.value}
              onChange={(e) =>
                handleUpdateFilter(filter.id, { value: e.target.value })
              }
              onKeyDown={handleKeyDown}
              placeholder={filter.operator === 'in' ? 'val1, val2, ...' : 'value'}
              className="bg-dbgo-input border rounded-sm px-2 py-0.5 text-xs text-text-primary placeholder:text-text-muted outline-none min-w-[80px] flex-1 max-w-[160px] focus:border-focus"
            />
          )}

          {/* Second value for BETWEEN */}
          {isBinaryOperator(filter.operator) && (
            <>
              <span className="text-2xs text-text-muted">and</span>
              <input
                type="text"
                value={filter.value2 ?? ''}
                onChange={(e) =>
                  handleUpdateFilter(filter.id, { value2: e.target.value })
                }
                onKeyDown={handleKeyDown}
                placeholder="value2"
                className="bg-dbgo-input border rounded-sm px-2 py-0.5 text-xs text-text-primary placeholder:text-text-muted outline-none min-w-[80px] max-w-[160px] focus:border-focus"
              />
            </>
          )}

          {/* Remove button */}
          <button
            onClick={() => handleRemoveFilter(filter.id)}
            className="w-5 h-5 flex items-center justify-center rounded-sm text-text-muted hover:text-accent-red hover:bg-dbgo-tab-hover transition-colors flex-shrink-0"
            title="Remove filter"
          >
            <X size={12} />
          </button>
        </div>
      ))}

      {/* Actions row */}
      <div className="flex items-center gap-2 pt-0.5">
        <button
          onClick={handleAddFilter}
          className="flex items-center gap-1 text-2xs text-text-secondary hover:text-text-primary hover:bg-dbgo-tab-hover px-1.5 py-0.5 rounded-sm transition-colors"
        >
          <Plus size={11} />
          Add Filter
        </button>

        {filters.length > 0 && (
          <>
            <div className="w-px h-3 bg-[var(--border-default)]" />
            <button
              onClick={handleApply}
              className="text-2xs text-accent-green hover:text-accent-green/80 px-1.5 py-0.5 rounded-sm hover:bg-accent-green/10 transition-colors"
            >
              Apply Filters
            </button>
            {whereClause && (
              <span className="text-[9px] text-text-muted font-mono truncate max-w-[300px]">
                {whereClause}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
